import type { ReactNode } from 'react';

/**
 * Keeps the end of a sentence together so it never wraps as a lone word or a lone character plus its
 * full stop. Lifted from the landing page and typed:
 * - Chinese: the last 4 characters (incl. punctuation) are held on one line.
 * - English: the last two words are joined with a non-breaking space.
 * Works together with the text-wrap rules in tokens.css.
 */
const CJK_END = /[⺀-鿿　-〿＀-￯]$/;

export function tidy(text: string): ReactNode;
export function tidy<T>(text: T): T | ReactNode;
export function tidy(text: unknown): ReactNode {
  if (typeof text !== 'string') return text as ReactNode;
  const s = text.trim();
  if (s.length < 8) return text;

  if (CJK_END.test(s)) {
    let cut = s.length - 4;
    // never split inside an embedded English word or number
    while (cut > 0 && /[A-Za-z0-9]/.test(s[cut]!) && /[A-Za-z0-9]/.test(s[cut - 1]!)) cut--;
    return (
      <>
        {s.slice(0, cut)}
        <span className="whitespace-nowrap">{s.slice(cut)}</span>
      </>
    );
  }

  const i = s.lastIndexOf(' ');
  if (i <= 0) return text;
  let out = `${s.slice(0, i)} ${s.slice(i + 1)}`;
  // very short tail ("it.", "to us.") — hold three words together instead
  if (out.length - i < 10) {
    const j = out.lastIndexOf(' ', i - 1);
    if (j > 0) out = `${out.slice(0, j)} ${out.slice(j + 1)}`;
  }
  return out;
}
